import { z } from "zod";
import { EscrowHistoryField } from "@remi/enums/escrow/history/field";
import { EscrowStatusSchema } from "@remi/enums/escrow-status";
import { EscrowVendorSchema } from "@remi/enums/escrow-vendor";
import { FundingMilestoneScheduleSchema } from "@remi/enums/funding-milestone-schedule";
import type { EqualWithDiff, Expect } from "@remi/ts-utils";

const variantByField = {
  status: z.object({
    field: z.literal(EscrowHistoryField.status),
    changedTo: EscrowStatusSchema,
    changedFrom: EscrowStatusSchema.nullable(),
  }),
  externalId: z.object({
    field: z.literal(EscrowHistoryField.externalId),
    changedTo: z.string(),
    changedFrom: z.string().nullable(),
  }),
  vendor: z.object({
    field: z.literal(EscrowHistoryField.vendor),
    changedTo: EscrowVendorSchema,
    changedFrom: EscrowVendorSchema.nullable(),
  }),
  customerInviteLink: z.object({
    field: z.literal(EscrowHistoryField.customerInviteLink),
    changedTo: z.string().nullable(),
    changedFrom: z.string().nullable(),
  }),
  requestForQuoteId: z.object({
    field: z.literal(EscrowHistoryField.requestForQuoteId),
    changedTo: z.coerce.number(),
    changedFrom: z.coerce.number().nullable(),
  }),
  contractAmount: z.object({
    field: z.literal(EscrowHistoryField.contractAmount),
    changedTo: z.coerce.number(),
    changedFrom: z.coerce.number(),
  }),
  fundedAmount: z.object({
    field: z.literal(EscrowHistoryField.fundedAmount),
    changedTo: z.coerce.number(),
    changedFrom: z.coerce.number(),
  }),
  pendingAmount: z.object({
    field: z.literal(EscrowHistoryField.pendingAmount),
    changedTo: z.coerce.number(),
    changedFrom: z.coerce.number(),
  }),
  disbursedAmount: z.object({
    field: z.literal(EscrowHistoryField.disbursedAmount),
    changedTo: z.coerce.number(),
    changedFrom: z.coerce.number(),
  }),
  fullyFundedDate: z.object({
    field: z.literal(EscrowHistoryField.fullyFundedDate),
    changedFrom: z.coerce.date().nullable(),
    changedTo: z.coerce.date().nullable(),
  }),
  fullyDisbursedDate: z.object({
    field: z.literal(EscrowHistoryField.fullyDisbursedDate),
    changedFrom: z.coerce.date().nullable(),
    changedTo: z.coerce.date().nullable(),
  }),
  cancellationFee: z.object({
    field: z.literal(EscrowHistoryField.cancellationFee),
    changedFrom: z.coerce.number().nullable(),
    changedTo: z.coerce.number().nullable(),
  }),
  fundingMilestoneSchedule: z.object({
    field: z.literal(EscrowHistoryField.fundingMilestoneSchedule),
    changedTo: FundingMilestoneScheduleSchema,
    changedFrom: FundingMilestoneScheduleSchema.nullable(),
  }),
  deletedAt: z.object({
    field: z.literal(EscrowHistoryField.deletedAt),
    changedFrom: z.coerce.date().nullable(),
    changedTo: z.coerce.date().nullable(),
  }),
} as const satisfies Record<EscrowHistoryField, z.ZodType>;

export const EscrowHistorySchema = z
  .object({
    id: z.string(),
    escrowId: z.string(),
    changedById: z.string(),
    createdAt: z.coerce.date(),
  })
  .and(
    z.discriminatedUnion("field", [
      variantByField.status,
      variantByField.externalId,
      variantByField.vendor,
      variantByField.customerInviteLink,
      variantByField.requestForQuoteId,
      variantByField.contractAmount,
      variantByField.fundedAmount,
      variantByField.pendingAmount,
      variantByField.disbursedAmount,
      variantByField.fullyFundedDate,
      variantByField.fullyDisbursedDate,
      variantByField.cancellationFee,
      variantByField.fundingMilestoneSchedule,
      variantByField.deletedAt,
    ]),
  );

type _EveryFieldIsReadable = Expect<EqualWithDiff<z.infer<typeof EscrowHistorySchema>["field"], EscrowHistoryField>>;

export type EscrowHistory = z.infer<typeof EscrowHistorySchema>;
